import { domainRouterClient } from '../../../api/domainRouterClient';
import { asArray, asRecord } from '../../../state/live-data/graphql';
import type { TuitionCollectionTrendDatum } from '../../source-faithful/shared/TuitionCollectionTrendChart';

type JsonRecord = Record<string, unknown>;

export type TuitionMirrorCorrelation = {
  providerEventId: string | null;
  eventType: string | null;
  stripeEventOccurredAt: string | null;
  paymentIntentId: string | null;
  checkoutSessionId: string | null;
  chargeId: string | null;
  disputeId: string | null;
};

export type TuitionTransactionSnapshot = {
  id: string;
  familyId: string | null;
  studentId: string | null;
  academicYear: string | null;
  entryType: string;
  amountCents: number;
  description: string | null;
  referenceCode: string | null;
  status: string | null;
  internalEffectiveDate: string | null;
  createdAt: string | null;
  createdBy: string | null;
  relatedEntryId: string | null;
  disputeId: string | null;
  disputeDeadline: string | null;
  isReversed: boolean;
  billingCategory: string | null;
  adhocInvoiceId: string | null;
  recurringFeeAssignmentId: string | null;
  source: string | null;
  observedAt: string | null;
  observedDateLabel: string;
  sourceKind: 'stripe-correlated' | 'internal-ledger';
  isStripeCorrelated: boolean;
  stripeCorrelation: TuitionMirrorCorrelation | null;
};

export type TuitionDueDateState = 'today' | 'upcoming' | 'overdue' | null;

export type TuitionStudentSummary = {
  id: string;
  name: string;
  gradeLabel: string;
  divisionLabel: string;
  rateCategory?: string | null;
  gender?: string | null;
  status?: string | null;
};

export type TuitionPayerSnapshot = {
  id: string;
  holderType: 'family' | 'parent' | 'third_party';
  displayName: string;
  relationLabel: string;
  email: string | null;
  isPrimary: boolean;
  allocationPercent: number;
  allocationBalanceCents: number;
};

export type ParentTuitionPaymentMethodSnapshot = {
  id: string;
  methodType: 'card' | 'bank_account' | 'payment_method';
  label: string | null;
  provider: string | null;
  lastFour: string | null;
  status: string | null;
  isPreferred: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  metadata: JsonRecord | null;
};

export type TuitionDocumentSnapshot = {
  id: string;
  name: string;
  fileName: string | null;
  category: string;
  status: string | null;
  uploadedAt: string | null;
  uploadedAtLabel: string;
  studentId: string | null;
  studentName: string | null;
  kind: 'receipt' | 'contract' | 'statement' | 'document';
  downloadMode: 'remote' | 'derived' | 'unavailable';
  remoteDocumentId: string | null;
  derivedTransactionId: string | null;
};

export type TuitionInstallmentSnapshot = {
  id: string;
  label: string;
  amountCents: number;
  remainingAmountCents: number;
  dueDate: string | null;
  dueDateLabel: string;
  status: 'paid' | 'due' | 'overdue' | 'upcoming';
  studentId: string | null;
  studentName: string | null;
};

export type TuitionFamilySnapshot = {
  familyId: string;
  familyName: string;
  studentCount: number;
  students: TuitionStudentSummary[];
  studentNames: string[];
  planLabel?: string | null;
  totalChargesCents: number;
  totalCreditsCents: number;
  netBalanceCents: number;
  currentDueCents: number;
  overdueCents: number;
  autoPayEnrolled: boolean;
  paymentMethodSummary: string;
  statusLabel: string;
  nextDueDate: string | null;
  nextDueDateLabel: string;
  nextDueDateState: TuitionDueDateState;
  payers: TuitionPayerSnapshot[];
  documents: TuitionDocumentSnapshot[];
  atRisk: boolean;
  transactions: TuitionTransactionSnapshot[];
  hasCorrelatedStripeData: boolean;
  latestObservedAt: string | null;
};

export type TuitionDashboardSnapshot = {
  academicYear: string;
  families: TuitionFamilySnapshot[];
  totalBilledCents: number;
  totalCollectedCents: number;
  outstandingCents: number;
  currentDueCents: number;
  overdueCents: number;
  autoPayFamilies: number;
  collectionRate: number;
  trend: TuitionCollectionTrendDatum[];
  trendAuthority: 'server' | 'legacy-absent';
  hasCorrelatedStripeData: boolean;
  latestObservedAt: string | null;
  isDataEmpty: boolean;
  emptyStateReason: 'no-family-accounts' | 'no-tuition-activity' | 'search-no-match' | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
};

export type ParentTuitionBreakdownLine = {
  id: string;
  label: string;
  amountCents: number;
  tone: 'charge' | 'credit' | 'aid';
};

export type ParentTuitionStudentSnapshot = {
  id: string;
  name: string;
  gradeLabel: string;
  divisionLabel: string;
  amountDueCents: number | null;
  subtotalCents: number;
  breakdown: ParentTuitionBreakdownLine[];
};

export type ParentTuitionSnapshot = {
  academicYear: string;
  familyId: string | null;
  familyName: string | null;
  totalChargesCents: number;
  totalCreditsCents: number;
  netBalanceCents: number;
  overdueCents: number;
  currentDueCents: number;
  autoPayEnrolled: boolean;
  paymentMethodSummary: string;
  paymentMethods: ParentTuitionPaymentMethodSnapshot[];
  students: ParentTuitionStudentSnapshot[];
  installmentSchedule: TuitionInstallmentSnapshot[];
  payerHolders: TuitionPayerSnapshot[];
  documents: TuitionDocumentSnapshot[];
  transactions: TuitionTransactionSnapshot[];
  hasCorrelatedStripeData: boolean;
  latestObservedAt: string | null;
};

const CHARGE_ENTRY_TYPES = new Set(['tuition', 'charge', 'registration_fee', 'security_fee', 'late_fee']);
const FEE_ENTRY_TYPES = new Set(['registration_fee', 'security_fee', 'late_fee']);
const BILLING_CATEGORY_FEE_TYPES = new Set(['fees', 'activities', 'transportation', 'food_service', 'recurring', 'other']);
const STUDENT_BREAKDOWN_ENTRY_TYPES = new Set([
  'tuition',
  'charge',
  'registration_fee',
  'security_fee',
  'late_fee',
  'scholarship',
  'credit',
  'adjustment',
]);
const MAX_TUITION_DASHBOARD_FAMILY_CONCURRENCY = 3;
const TUITION_ROUTER_RETRY_DELAYS_MS = [150, 350];

const normalizeText = (value: unknown): string =>
  String(value ?? '').trim();

const normalizeOptionalText = (value: unknown): string | null => {
  const normalized = normalizeText(value);
  return normalized || null;
};

const readPath = (value: unknown, path: readonly (string | number)[]): unknown => {
  let current: unknown = value;
  for (const segment of path) {
    if (typeof segment === 'number') {
      current = asArray(current)[segment];
      continue;
    }
    current = asRecord(current)[segment];
  }
  return current;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const normalizeIsoMoment = (value: unknown): string | null => {
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/u.test(normalized)) {
    return `${normalized}T00:00:00.000Z`;
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const toIsoDate = (value: unknown): string | null => {
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    return null;
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString().slice(0, 10);
};

const delay = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const sortIsoDescending = (left: string | null, right: string | null): number => {
  const leftEpoch = left ? Date.parse(left) : 0;
  const rightEpoch = right ? Date.parse(right) : 0;
  return rightEpoch - leftEpoch;
};

const isTransientTuitionRouterError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const normalized = message.toLowerCase();
  return normalized.includes('remaining connection slots are reserved')
    || normalized.includes('too many clients already')
    || normalized.includes('connection terminated unexpectedly')
    || normalized.includes('server closed the connection unexpectedly')
    || normalized.includes('timed out after');
};

const runWithTuitionRouterRetry = async <T>(
  label: string,
  run: () => Promise<T>,
): Promise<T> => {
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= TUITION_ROUTER_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      lastError = error;
      if (!isTransientTuitionRouterError(error) || attempt >= TUITION_ROUTER_RETRY_DELAYS_MS.length) {
        break;
      }
      const retryDelayMs = TUITION_ROUTER_RETRY_DELAYS_MS[attempt] ?? 0;
      console.warn(`[tuition-data] retrying ${label} after transient router failure: ${error instanceof Error ? error.message : String(error)}`);
      await delay(retryDelayMs);
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`${label} failed.`);
};

const queryTuitionRouter = async <TData>(
  operation: Parameters<typeof domainRouterClient.query>[0],
  args: Record<string, unknown>,
): Promise<TData> =>
  runWithTuitionRouterRetry(
    `query:${String(operation)}`,
    () => domainRouterClient.query(operation, args as never) as Promise<TData>,
  );

const mapWithConcurrency = async <TInput, TOutput>(
  items: TInput[],
  concurrency: number,
  mapper: (item: TInput, index: number) => Promise<TOutput>,
): Promise<TOutput[]> => {
  const results = new Array<TOutput>(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, Math.max(items.length, 1)) }, () => worker()),
  );
  return results;
};

const todayIsoDate = (): string => new Date().toISOString().slice(0, 10);

const formatDivision = (value: unknown): string => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) {
    return 'School';
  }
  if (normalized.includes('preschool')) {
    return 'Preschool';
  }
  if (normalized.includes('girls')) {
    return 'Girls School';
  }
  if (normalized.includes('boys')) {
    return 'Boys School';
  }
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const formatGrade = (value: unknown): string => {
  const normalized = normalizeText(value);
  return normalized || 'Unassigned';
};

const buildMonthKeys = (count = 6): string[] => {
  const cursor = new Date();
  cursor.setDate(1);
  const months: string[] = [];
  for (let index = count - 1; index >= 0; index -= 1) {
    const date = new Date(cursor.getFullYear(), cursor.getMonth() - index, 1);
    months.push(date.toISOString().slice(0, 7));
  }
  return months;
};

const monthLabel = (monthKey: string): string =>
  new Date(`${monthKey}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });

const formatDate = (value: unknown): string => {
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    return 'Date unavailable';
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const toTitleCase = (value: string): string =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
  || 'Unknown';

const extractMirrorCorrelation = (mirrorEntry: JsonRecord): TuitionMirrorCorrelation | null => {
  const payload = asRecord(mirrorEntry.payload);
  if (!payload || Object.keys(payload).length === 0) {
    return null;
  }
  const eventType = normalizeOptionalText(mirrorEntry.eventType);
  const paymentIntentId =
    normalizeOptionalText(readPath(payload, ['data', 'object', 'payment_intent']))
    ?? (eventType?.startsWith('payment_intent.')
      ? normalizeOptionalText(readPath(payload, ['data', 'object', 'id']))
      : null)
    ?? normalizeOptionalText(readPath(payload, ['data', 'object', 'charge', 'payment_intent']));
  const checkoutSessionId =
    readPath(payload, ['data', 'object', 'object']) === 'checkout.session'
      ? normalizeOptionalText(readPath(payload, ['data', 'object', 'id']))
      : null;
  const chargeId =
    normalizeOptionalText(readPath(payload, ['data', 'object', 'charge']))
    ?? (readPath(payload, ['data', 'object', 'object']) === 'charge'
      ? normalizeOptionalText(readPath(payload, ['data', 'object', 'id']))
      : null);
  const disputeId =
    readPath(payload, ['data', 'object', 'object']) === 'dispute'
      ? normalizeOptionalText(readPath(payload, ['data', 'object', 'id']))
      : null;

  return {
    providerEventId: normalizeOptionalText(mirrorEntry.providerEventId),
    eventType,
    stripeEventOccurredAt: Number.isFinite(Number(mirrorEntry.occurredAt))
      ? new Date(Number(mirrorEntry.occurredAt) * 1000).toISOString()
      : null,
    paymentIntentId,
    checkoutSessionId,
    chargeId,
    disputeId,
  };
};

const buildMirrorReferenceKeys = (
  eventType: string | null,
  correlation: TuitionMirrorCorrelation,
): string[] => {
  const keys = new Set<string>();
  if (
    eventType === 'checkout.session.completed'
    || eventType === 'checkout.session.async_payment_succeeded'
    || eventType === 'payment_intent.succeeded'
    || eventType === 'invoice.paid'
  ) {
    if (correlation.paymentIntentId) {
      keys.add(`stripe:tuition:payment:${correlation.paymentIntentId}`);
    }
    if (correlation.checkoutSessionId) {
      keys.add(`stripe:tuition:payment:${correlation.checkoutSessionId}`);
    }
  }
  if (eventType === 'charge.refunded') {
    if (correlation.paymentIntentId) {
      keys.add(`stripe:tuition:refund:${correlation.paymentIntentId}`);
    }
    if (correlation.chargeId) {
      keys.add(`stripe:tuition:refund:${correlation.chargeId}`);
    }
  }
  if (eventType === 'charge.dispute.created') {
    for (const candidate of [
      correlation.disputeId,
      correlation.chargeId,
      correlation.paymentIntentId,
    ]) {
      if (candidate) {
        keys.add(`stripe:tuition:dispute:${candidate}`);
      }
    }
  }
  if (eventType === 'charge.dispute.closed') {
    for (const candidate of [
      correlation.disputeId,
      correlation.chargeId,
      correlation.paymentIntentId,
    ]) {
      if (candidate) {
        keys.add(`stripe:tuition:dispute-close:${candidate}`);
      }
    }
  }
  return Array.from(keys);
};

const buildMirrorCorrelationIndex = (
  mirrorItems: JsonRecord[],
): Map<string, TuitionMirrorCorrelation> => {
  const index = new Map<string, TuitionMirrorCorrelation>();
  for (const item of mirrorItems) {
    const correlation = extractMirrorCorrelation(item);
    if (!correlation) {
      continue;
    }
    for (const key of buildMirrorReferenceKeys(correlation.eventType, correlation)) {
      if (!index.has(key)) {
        index.set(key, correlation);
      }
    }
  }
  return index;
};

const loadTuitionMirrorCorrelationIndex = async (): Promise<Map<string, TuitionMirrorCorrelation>> => {
  try {
    const mirrorRaw = await queryTuitionRouter<JsonRecord>('listPaymentMirrorLedgerEntries', {
      input: {
        moduleId: 'tuition',
        limit: 100,
      },
    });
    const items = asArray<JsonRecord>(asRecord(mirrorRaw).items);
    return buildMirrorCorrelationIndex(items);
  } catch (error) {
    console.warn(
      `[tuition-data] continuing without payment mirror correlation: ${error instanceof Error ? error.message : String(error)}`,
    );
    return new Map();
  }
};

const normalizeTransactionSnapshot = (
  transaction: JsonRecord,
  mirrorIndex: Map<string, TuitionMirrorCorrelation>,
): TuitionTransactionSnapshot => {
  const referenceCode = normalizeOptionalText(transaction.referenceCode);
  const stripeCorrelation = referenceCode ? mirrorIndex.get(referenceCode) ?? null : null;
  const internalEffectiveDate = normalizeIsoMoment(transaction.effectiveDate);
  const createdAt = normalizeIsoMoment(transaction.createdAt);
  const observedAt = stripeCorrelation?.stripeEventOccurredAt ?? internalEffectiveDate ?? createdAt;
  return {
    id: normalizeText(transaction.id) || referenceCode || `${normalizeText(transaction.entryType)}:${normalizeText(transaction.effectiveDate)}`,
    familyId: normalizeOptionalText(transaction.familyId),
    studentId: normalizeOptionalText(transaction.studentId),
    academicYear: normalizeOptionalText(transaction.academicYear),
    entryType: normalizeText(transaction.entryType),
    amountCents: Math.round(toNumber(transaction.amount)),
    description: normalizeOptionalText(transaction.description),
    referenceCode,
    status: normalizeOptionalText(transaction.status),
    internalEffectiveDate,
    createdAt,
    createdBy: normalizeOptionalText(transaction.createdBy),
    relatedEntryId: normalizeOptionalText(transaction.relatedEntryId),
    disputeId: normalizeOptionalText(transaction.disputeId),
    disputeDeadline: normalizeIsoMoment(transaction.disputeDeadline),
    isReversed: Boolean(transaction.isReversed),
    billingCategory: normalizeOptionalText(transaction.billingCategory),
    adhocInvoiceId: normalizeOptionalText(transaction.adhocInvoiceId),
    recurringFeeAssignmentId: normalizeOptionalText(transaction.recurringFeeAssignmentId),
    source: normalizeOptionalText(transaction.source),
    observedAt,
    observedDateLabel: formatDate(observedAt ?? internalEffectiveDate ?? createdAt),
    sourceKind: stripeCorrelation ? 'stripe-correlated' : 'internal-ledger',
    isStripeCorrelated: Boolean(stripeCorrelation),
    stripeCorrelation,
  };
};

const normalizeTransactions = (
  transactions: JsonRecord[],
  mirrorIndex: Map<string, TuitionMirrorCorrelation>,
): TuitionTransactionSnapshot[] =>
  transactions
    .map((transaction) => normalizeTransactionSnapshot(transaction, mirrorIndex))
    .sort((left, right) => {
      const observedSort = sortIsoDescending(left.observedAt, right.observedAt);
      if (observedSort !== 0) {
        return observedSort;
      }
      return sortIsoDescending(left.createdAt, right.createdAt);
    });

const normalizeParentPaymentMethodSnapshot = (
  method: JsonRecord,
): ParentTuitionPaymentMethodSnapshot => {
  const metadata = asRecord(method.metadata);

  return {
    id: normalizeText(method.id),
    methodType: normalizeText(method.methodType) === 'bank_account'
      ? 'bank_account'
      : normalizeText(method.methodType) === 'card'
        ? 'card'
        : 'payment_method',
    label: normalizeOptionalText(method.label),
    provider: normalizeOptionalText(method.provider),
    lastFour: normalizeOptionalText(method.lastFour),
    status: normalizeOptionalText(method.status),
    isPreferred: Boolean(method.preferred),
    createdAt: normalizeIsoMoment(method.createdAt),
    updatedAt: normalizeIsoMoment(method.updatedAt),
    metadata: Object.keys(metadata).length > 0 ? metadata : null,
  };
};

const summarizePaymentMethods = (
  methods: ParentTuitionPaymentMethodSnapshot[],
): { autoPayEnrolled: boolean; paymentMethodSummary: string } => {
  const activeMethods = methods.filter((method) => normalizeText(method.status || 'active') !== 'inactive');
  const preferredMethod = activeMethods.find((method) => method.isPreferred) ?? activeMethods[0] ?? null;
  if (!preferredMethod) {
    return {
      autoPayEnrolled: false,
      paymentMethodSummary: 'No active payment method on file',
    };
  }

  const methodType = normalizeText(preferredMethod.methodType).replace(/_/g, ' ') || 'payment method';
  const lastFour = preferredMethod.lastFour;
  const provider = preferredMethod.provider;
  const label = [
    provider,
    methodType.replace(/\b\w/g, (segment) => segment.toUpperCase()),
    lastFour ? `•••• ${lastFour}` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    autoPayEnrolled: true,
    paymentMethodSummary: label || 'Payment method on file',
  };
};

const buildStudentDisplayName = (student: JsonRecord): string => {
  const preferredName = normalizeOptionalText(student.preferredName);
  const firstName = normalizeText(student.firstName);
  const lastName = normalizeText(student.lastName);
  return [preferredName || firstName, lastName].filter(Boolean).join(' ').trim();
};

const buildStudentSummary = (student: JsonRecord): TuitionStudentSummary => ({
  id: normalizeText(student.id),
  name: buildStudentDisplayName(student) || 'Student',
  gradeLabel: formatGrade(student.grade ?? student.gradeApplyingFor),
  divisionLabel: formatDivision(student.division ?? student.schoolDivision),
  rateCategory: normalizeOptionalText(student.rateCategory),
  gender: normalizeOptionalText(student.gender),
  status: normalizeOptionalText(student.status),
});

const buildStudentNameIndex = (students: TuitionStudentSummary[]): Map<string, string> =>
  new Map(
    students
      .map((student) => [student.id, student.name] as const)
      .filter(([id]) => Boolean(id)),
  );

const buildParentContactDisplayName = (contact: JsonRecord): string => {
  const firstName = normalizeText(contact.firstName);
  const lastName = normalizeText(contact.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || normalizeText(contact.email) || 'Parent payer';
};

const buildPayerSnapshots = (
  sources: Array<{
    id: string;
    holderType: TuitionPayerSnapshot['holderType'];
    displayName: string;
    relationLabel: string;
    email?: string | null;
    isPrimary?: boolean;
  }>,
  totalBalanceCents: number,
): TuitionPayerSnapshot[] => {
  const normalizedSources = sources.filter((source) => source.id && source.displayName);
  if (normalizedSources.length === 0) {
    return [];
  }
  return normalizedSources.map((source, index) => {
    const allocationPercent = index === normalizedSources.length - 1
      ? 100 - normalizedSources.slice(0, index).reduce((sum, item) => sum + Math.floor(100 / normalizedSources.length), 0)
      : Math.floor(100 / normalizedSources.length);
    return {
      id: source.id,
      holderType: source.holderType,
      displayName: source.displayName,
      relationLabel: source.relationLabel,
      email: normalizeOptionalText(source.email),
      isPrimary: Boolean(source.isPrimary ?? index === 0),
      allocationPercent,
      allocationBalanceCents: Math.round((Math.max(totalBalanceCents, 0) * allocationPercent) / 100),
    };
  });
};

const resolveDocumentKind = (document: JsonRecord): TuitionDocumentSnapshot['kind'] => {
  const corpus = [
    normalizeText(document.name),
    normalizeText(document.fileName),
    normalizeText(document.category),
    normalizeText(document.status),
  ].join(' ').toLowerCase();
  if (corpus.includes('receipt')) {
    return 'receipt';
  }
  if (corpus.includes('contract')) {
    return 'contract';
  }
  if (corpus.includes('statement')) {
    return 'statement';
  }
  return 'document';
};

const buildDocumentSnapshots = (
  rawDocuments: JsonRecord[],
  studentNameById: Map<string, string>,
): TuitionDocumentSnapshot[] =>
  rawDocuments
    .map((document) => {
      const studentId = normalizeOptionalText(document.studentId);
      return {
        id: normalizeText(document.id),
        name: normalizeText(document.name) || 'Document',
        fileName: normalizeOptionalText(document.fileName),
        category: toTitleCase(normalizeText(document.category) || 'document'),
        status: normalizeOptionalText(document.status),
        uploadedAt: normalizeIsoMoment(document.uploadedAt),
        uploadedAtLabel: formatDate(document.uploadedAt),
        studentId,
        studentName: studentId ? studentNameById.get(studentId) ?? null : null,
        kind: resolveDocumentKind(document),
        downloadMode: document.storageKey || document.resourceId
          ? 'remote' as const
          : 'unavailable' as const,
        remoteDocumentId: normalizeOptionalText(document.id),
        derivedTransactionId: null,
      };
    })
    .sort((left, right) => sortIsoDescending(left.uploadedAt, right.uploadedAt));

const buildDerivedReceiptArtifacts = (
  transactions: TuitionTransactionSnapshot[],
  studentNameById: Map<string, string>,
): TuitionDocumentSnapshot[] =>
  transactions
    .filter((transaction) => transaction.entryType === 'payment' || transaction.entryType === 'refund')
    .map((transaction): TuitionDocumentSnapshot => ({
      id: `receipt:${transaction.id}`,
      name: transaction.entryType === 'refund'
        ? `Refund receipt ${transaction.observedDateLabel}`
        : `Payment receipt ${transaction.observedDateLabel}`,
      fileName: `${transaction.entryType === 'refund' ? 'refund' : 'receipt'}-${transaction.id}.txt`,
      category: 'Receipt',
      status: transaction.isStripeCorrelated ? 'ready' : 'recorded',
      uploadedAt: transaction.observedAt ?? transaction.createdAt,
      uploadedAtLabel: transaction.observedDateLabel,
      studentId: transaction.studentId,
      studentName: transaction.studentId ? studentNameById.get(transaction.studentId) ?? null : null,
      kind: 'receipt' as const,
      downloadMode: 'derived' as const,
      remoteDocumentId: null,
      derivedTransactionId: transaction.id,
    }))
    .sort((left, right) => sortIsoDescending(left.uploadedAt, right.uploadedAt));

type AllocatedTuitionCharge = {
  id: string;
  entryType: string;
  amountCents: number;
  remainingAmountCents: number;
  effectiveDate: string;
  studentId: string | null;
};

type TuitionOpenBalanceSummary = {
  currentDueCents: number;
  overdueCents: number;
  nextDueDate: string | null;
  nextDueDateLabel: string;
  nextDueDateState: TuitionDueDateState;
  openCharges: AllocatedTuitionCharge[];
};

const resolveChargeEffectiveDate = (transaction: JsonRecord): string | null =>
  toIsoDate(transaction.effectiveDate ?? transaction.internalEffectiveDate ?? transaction.createdAt);

const summarizeOpenBalance = (
  rawTransactions: JsonRecord[],
  evaluationIsoDate = todayIsoDate(),
): TuitionOpenBalanceSummary => {
  const charges = rawTransactions
    .filter((transaction) => {
      const status = normalizeText(transaction.status);
      const amountCents = Math.round(toNumber(transaction.amount));
      return status !== 'voided' && amountCents > 0;
    })
    .map((transaction) => ({
      id: normalizeText(transaction.id),
      entryType: normalizeText(transaction.entryType),
      amountCents: Math.round(toNumber(transaction.amount)),
      remainingAmountCents: Math.round(toNumber(transaction.amount)),
      effectiveDate: resolveChargeEffectiveDate(transaction),
      studentId: normalizeOptionalText(transaction.studentId),
    }))
    .filter((transaction): transaction is AllocatedTuitionCharge => Boolean(transaction.effectiveDate))
    .sort((left, right) => left.effectiveDate.localeCompare(right.effectiveDate));

  let remainingCreditsCents = Math.abs(
    rawTransactions.reduce((sum, transaction) => {
      const amountCents = Math.round(toNumber(transaction.amount));
      const status = normalizeText(transaction.status);
      if (status === 'voided' || amountCents >= 0) {
        return sum;
      }
      return sum + amountCents;
    }, 0),
  );

  const openCharges = charges
    .map((transaction) => {
      const appliedCreditsCents = Math.min(remainingCreditsCents, transaction.remainingAmountCents);
      remainingCreditsCents -= appliedCreditsCents;
      return {
        ...transaction,
        remainingAmountCents: Math.max(transaction.remainingAmountCents - appliedCreditsCents, 0),
      };
    })
    .filter((transaction) => transaction.remainingAmountCents > 0);

  if (openCharges.length === 0) {
    return {
      currentDueCents: 0,
      overdueCents: 0,
      nextDueDate: null,
      nextDueDateLabel: 'Paid in full',
      nextDueDateState: null,
      openCharges,
    };
  }

  const overdueCharges = openCharges.filter((transaction) => transaction.effectiveDate < evaluationIsoDate);
  const currentCharges = openCharges.filter((transaction) => transaction.effectiveDate >= evaluationIsoDate);
  const nextCharge = currentCharges[0] ?? overdueCharges[0] ?? null;

  return {
    currentDueCents: currentCharges.reduce((sum, transaction) => sum + transaction.remainingAmountCents, 0),
    overdueCents: overdueCharges.reduce((sum, transaction) => sum + transaction.remainingAmountCents, 0),
    nextDueDate: nextCharge?.effectiveDate ?? null,
    nextDueDateLabel: nextCharge ? formatDate(nextCharge.effectiveDate) : 'Open balance',
    nextDueDateState: nextCharge == null
      ? null
      : nextCharge.effectiveDate === evaluationIsoDate
        ? 'today'
        : nextCharge.effectiveDate > evaluationIsoDate
          ? 'upcoming'
          : 'overdue',
    openCharges,
  };
};

const buildInstallmentSchedule = (
  rawTransactions: JsonRecord[],
  studentNameById: Map<string, string>,
): TuitionInstallmentSnapshot[] => {
  const openBalanceSummary = summarizeOpenBalance(rawTransactions);
  const openChargeById = new Map(openBalanceSummary.openCharges.map((charge) => [charge.id, charge] as const));
  const charges = rawTransactions
    .filter((transaction) => {
      const entryType = normalizeText(transaction.entryType);
      const status = normalizeText(transaction.status);
      return CHARGE_ENTRY_TYPES.has(entryType) && status !== 'voided' && Math.round(toNumber(transaction.amount)) > 0;
    })
    .map((transaction) => {
      const id = normalizeText(transaction.id);
      const openCharge = openChargeById.get(id);
      return {
        id,
        entryType: normalizeText(transaction.entryType),
        amountCents: Math.round(toNumber(transaction.amount)),
        remainingAmountCents: openCharge?.remainingAmountCents ?? 0,
        dueDate: resolveChargeEffectiveDate(transaction),
        studentId: normalizeOptionalText(transaction.studentId),
      };
    })
    .sort((left, right) => (left.dueDate ?? '').localeCompare(right.dueDate ?? ''));
  const today = todayIsoDate();

  return charges.map((transaction, index) => {
    const amountCents = transaction.amountCents;
    const remainingAmountCents = transaction.remainingAmountCents;
    const dueDate = transaction.dueDate;
    const studentId = transaction.studentId;
    let status: TuitionInstallmentSnapshot['status'] = 'paid';
    if (remainingAmountCents > 0) {
      if (dueDate && dueDate < today) {
        status = 'overdue';
      } else if (dueDate === today) {
        status = 'due';
      } else {
        status = 'upcoming';
      }
    }

    return {
      id: transaction.id || `installment-${index + 1}`,
      label: resolveBreakdownLabel(
        transaction.entryType,
        normalizeOptionalText((rawTransactions.find((candidate) => normalizeText(candidate.id) === transaction.id) ?? {}).billingCategory),
      ),
      amountCents,
      remainingAmountCents,
      dueDate,
      dueDateLabel: dueDate ? formatDate(dueDate) : 'Date unavailable',
      status,
      studentId,
      studentName: studentId ? studentNameById.get(studentId) ?? null : null,
    };
  });
};

const buildTrend = (transactions: TuitionTransactionSnapshot[]): TuitionCollectionTrendDatum[] => {
  const monthKeys = buildMonthKeys(6);
  const buckets = new Map<string, { tuition: number; fees: number; collected: number }>(
    monthKeys.map((key) => [key, { tuition: 0, fees: 0, collected: 0 }]),
  );

  for (const transaction of transactions) {
    const entryType = transaction.entryType;
    const amountCents = Math.abs(transaction.amountCents);
    if (
      entryType === 'payment'
      || entryType === 'refund'
      || entryType === 'reversal'
      || entryType === 'chargeback'
      || entryType === 'adjustment'
    ) {
      const observedDate = toIsoDate(transaction.observedAt);
      if (!observedDate) {
        continue;
      }
      const monthKey = observedDate.slice(0, 7);
      const bucket = buckets.get(monthKey);
      if (!bucket) {
        continue;
      }
      if (transaction.amountCents < 0) {
        bucket.collected += amountCents;
      } else {
        bucket.collected -= amountCents;
      }
      continue;
    }
    if (!CHARGE_ENTRY_TYPES.has(entryType)) {
      continue;
    }
    const effectiveDate = toIsoDate(transaction.internalEffectiveDate);
    if (!effectiveDate) {
      continue;
    }
    const monthKey = effectiveDate.slice(0, 7);
    const bucket = buckets.get(monthKey);
    if (!bucket) {
      continue;
    }
    if (
      FEE_ENTRY_TYPES.has(entryType)
      || (
        entryType === 'charge'
        && BILLING_CATEGORY_FEE_TYPES.has(normalizeText(transaction.billingCategory))
      )
    ) {
      bucket.fees += amountCents;
    } else {
      bucket.tuition += amountCents;
    }
  }

  return monthKeys.map((monthKey) => {
    const bucket = buckets.get(monthKey) ?? { tuition: 0, fees: 0, collected: 0 };
    return {
      month: monthLabel(monthKey),
      tuition: Math.round(bucket.tuition / 100000),
      fees: Math.round(bucket.fees / 100000),
      collected: Math.round(bucket.collected / 100000),
    };
  });
};

const resolveBreakdownLabel = (entryType: string, billingCategory?: string | null): string => {
  switch (entryType) {
    case 'tuition':
      return 'Base Tuition';
    case 'charge':
      return billingCategory ? `${toTitleCase(billingCategory)} Charge` : 'Ad-hoc Charge';
    case 'registration_fee':
      return 'Registration Fee';
    case 'security_fee':
      return 'Security Fee';
    case 'late_fee':
      return 'Late Fee';
    case 'scholarship':
      return 'Financial Aid';
    case 'credit':
      return 'Credit';
    case 'adjustment':
      return 'Adjustment';
    default:
      return entryType
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ') || 'Ledger entry';
  }
};

const resolveBreakdownTone = (entryType: string, amountCents: number): ParentTuitionBreakdownLine['tone'] => {
  if (entryType === 'scholarship') {
    return 'aid';
  }
  if (amountCents < 0 || entryType === 'credit' || entryType === 'adjustment') {
    return 'credit';
  }
  return 'charge';
};

const buildStudentBreakdown = (
  transactions: JsonRecord[],
  studentId: string,
): ParentTuitionBreakdownLine[] => {
  const grouped = new Map<string, ParentTuitionBreakdownLine>();

  for (const transaction of transactions) {
    const transactionStudentId = normalizeOptionalText(transaction.studentId);
    if (transactionStudentId !== studentId) {
      continue;
    }
    const entryType = normalizeText(transaction.entryType);
    if (!STUDENT_BREAKDOWN_ENTRY_TYPES.has(entryType)) {
      continue;
    }

    const amountCents = Math.round(toNumber(transaction.amount));
    const label = resolveBreakdownLabel(entryType, normalizeOptionalText(transaction.billingCategory));
    const key = `${entryType}:${label}`;
    const current = grouped.get(key);

    if (current) {
      current.amountCents += amountCents;
      continue;
    }

    grouped.set(key, {
      id: key,
      label,
      amountCents,
      tone: resolveBreakdownTone(entryType, amountCents),
    });
  }

  return Array.from(grouped.values());
};

const resolveTransactionDateRange = (
  transactions: TuitionTransactionSnapshot[],
): { dateRangeStart: string | null; dateRangeEnd: string | null; latestObservedAt: string | null } => {
  const observedMoments = transactions
    .map((transaction) => transaction.observedAt)
    .filter((value): value is string => Boolean(value))
    .sort(sortIsoDescending);

  if (observedMoments.length === 0) {
    return {
      dateRangeStart: null,
      dateRangeEnd: null,
      latestObservedAt: null,
    };
  }

  return {
    dateRangeStart: observedMoments[observedMoments.length - 1] ?? null,
    dateRangeEnd: observedMoments[0] ?? null,
    latestObservedAt: observedMoments[0] ?? null,
  };
};

const normalizeTransactionSnapshotFromTree = (
  transaction: JsonRecord,
): TuitionTransactionSnapshot => {
  const stripeCorrelationRecord = asRecord(transaction.stripeCorrelation);
  return {
    id: normalizeText(transaction.id),
    familyId: normalizeOptionalText(transaction.familyId),
    studentId: normalizeOptionalText(transaction.studentId),
    academicYear: normalizeOptionalText(transaction.academicYear),
    entryType: normalizeText(transaction.entryType),
    amountCents: Math.round(toNumber(transaction.amountCents)),
    description: normalizeOptionalText(transaction.description),
    referenceCode: normalizeOptionalText(transaction.referenceCode),
    status: normalizeOptionalText(transaction.status),
    internalEffectiveDate: normalizeIsoMoment(transaction.internalEffectiveDate),
    createdAt: normalizeIsoMoment(transaction.createdAt),
    createdBy: normalizeOptionalText(transaction.createdBy),
    relatedEntryId: normalizeOptionalText(transaction.relatedEntryId),
    disputeId: normalizeOptionalText(transaction.disputeId),
    disputeDeadline: normalizeIsoMoment(transaction.disputeDeadline),
    isReversed: Boolean(transaction.isReversed),
    billingCategory: normalizeOptionalText(transaction.billingCategory),
    adhocInvoiceId: normalizeOptionalText(transaction.adhocInvoiceId),
    recurringFeeAssignmentId: normalizeOptionalText(transaction.recurringFeeAssignmentId),
    source: normalizeOptionalText(transaction.source),
    observedAt: normalizeIsoMoment(transaction.observedAt),
    observedDateLabel: normalizeText(transaction.observedDateLabel) || formatDate(transaction.observedAt),
    sourceKind: transaction.sourceKind === 'stripe-correlated' ? 'stripe-correlated' : 'internal-ledger',
    isStripeCorrelated: Boolean(transaction.isStripeCorrelated),
    stripeCorrelation: stripeCorrelationRecord ? {
      providerEventId: normalizeOptionalText(stripeCorrelationRecord.providerEventId),
      eventType: normalizeOptionalText(stripeCorrelationRecord.eventType),
      stripeEventOccurredAt: normalizeIsoMoment(stripeCorrelationRecord.stripeEventOccurredAt),
      paymentIntentId: normalizeOptionalText(stripeCorrelationRecord.paymentIntentId),
      checkoutSessionId: normalizeOptionalText(stripeCorrelationRecord.checkoutSessionId),
      chargeId: normalizeOptionalText(stripeCorrelationRecord.chargeId),
      disputeId: normalizeOptionalText(stripeCorrelationRecord.disputeId),
    } : null,
  };
};

const normalizeStudentSummaryFromTree = (
  student: JsonRecord,
): TuitionStudentSummary => ({
  id: normalizeText(student.id),
  name: normalizeText(student.name) || 'Student',
  gradeLabel: normalizeText(student.gradeLabel) || 'Unassigned',
  divisionLabel: normalizeText(student.divisionLabel) || 'School',
  rateCategory: normalizeOptionalText(student.rateCategory),
  gender: normalizeOptionalText(student.gender),
  status: normalizeOptionalText(student.status),
});

const normalizePayerSnapshotFromTree = (
  payer: JsonRecord,
): TuitionPayerSnapshot => ({
  id: normalizeText(payer.id),
  holderType: payer.holderType === 'parent'
    ? 'parent'
    : payer.holderType === 'third_party'
      ? 'third_party'
      : 'family',
  displayName: normalizeText(payer.displayName) || 'Payer',
  relationLabel: normalizeText(payer.relationLabel) || 'Household',
  email: normalizeOptionalText(payer.email),
  isPrimary: Boolean(payer.isPrimary),
  allocationPercent: toNumber(payer.allocationPercent),
  allocationBalanceCents: Math.round(toNumber(payer.allocationBalanceCents)),
});

const normalizeDocumentSnapshotFromTree = (
  document: JsonRecord,
): TuitionDocumentSnapshot => ({
  id: normalizeText(document.id),
  name: normalizeText(document.name) || 'Document',
  fileName: normalizeOptionalText(document.fileName),
  category: normalizeText(document.category) || 'Document',
  status: normalizeOptionalText(document.status),
  uploadedAt: normalizeIsoMoment(document.uploadedAt),
  uploadedAtLabel: normalizeText(document.uploadedAtLabel) || formatDate(document.uploadedAt),
  studentId: normalizeOptionalText(document.studentId),
  studentName: normalizeOptionalText(document.studentName),
  kind: document.kind === 'receipt'
    ? 'receipt'
    : document.kind === 'contract'
      ? 'contract'
      : document.kind === 'statement'
        ? 'statement'
        : 'document',
  downloadMode: document.downloadMode === 'remote'
    ? 'remote'
    : document.downloadMode === 'derived'
      ? 'derived'
      : 'unavailable',
  remoteDocumentId: normalizeOptionalText(document.remoteDocumentId),
  derivedTransactionId: normalizeOptionalText(document.derivedTransactionId),
});

const normalizeFamilySnapshotFromTree = (
  family: JsonRecord,
): TuitionFamilySnapshot => ({
  familyId: normalizeText(family.familyId),
  familyName: normalizeText(family.familyName) || 'Family',
  studentCount: Math.max(Math.round(toNumber(family.studentCount)), 0),
  students: asArray<JsonRecord>(family.students).map((student) => normalizeStudentSummaryFromTree(student)),
  studentNames: asArray(family.studentNames)
    .map((entry) => normalizeText(entry))
    .filter(Boolean),
  planLabel: normalizeOptionalText(family.planLabel),
  totalChargesCents: Math.round(toNumber(family.totalChargesCents)),
  totalCreditsCents: Math.round(toNumber(family.totalCreditsCents)),
  netBalanceCents: Math.round(toNumber(family.netBalanceCents)),
  currentDueCents: Math.round(toNumber(family.currentDueCents)),
  overdueCents: Math.round(toNumber(family.overdueCents)),
  autoPayEnrolled: Boolean(family.autoPayEnrolled),
  paymentMethodSummary: normalizeText(family.paymentMethodSummary) || 'No active payment method on file',
  statusLabel: normalizeText(family.statusLabel) || 'Current',
  nextDueDate: normalizeOptionalText(family.nextDueDate),
  nextDueDateLabel: normalizeText(family.nextDueDateLabel) || 'Open balance',
  nextDueDateState: family.nextDueDateState === 'today'
    ? 'today'
    : family.nextDueDateState === 'upcoming'
      ? 'upcoming'
      : family.nextDueDateState === 'overdue'
        ? 'overdue'
        : null,
  payers: asArray<JsonRecord>(family.payers).map((payer) => normalizePayerSnapshotFromTree(payer)),
  documents: asArray<JsonRecord>(family.documents).map((document) => normalizeDocumentSnapshotFromTree(document)),
  atRisk: Boolean(family.atRisk),
  transactions: asArray<JsonRecord>(family.transactions).map((transaction) => normalizeTransactionSnapshotFromTree(transaction)),
  hasCorrelatedStripeData: Boolean(family.hasCorrelatedStripeData),
  latestObservedAt: normalizeIsoMoment(family.latestObservedAt),
});

export type TuitionFamilyLedgerSnapshot = {
  academicYear: string;
  family: TuitionFamilySnapshot;
  ledgerEntries: TuitionTransactionSnapshot[];
  payers: TuitionPayerSnapshot[];
  documents: TuitionDocumentSnapshot[];
  summary: {
    totalChargesCents: number;
    totalCreditsCents: number;
    netBalanceCents: number;
    currentDueCents: number;
    overdueCents: number;
    studentCount: number;
    autoPayEnrolled: boolean;
    paymentMethodSummary: string;
    statusLabel: string;
    nextDueDate: string | null;
    nextDueDateLabel: string;
    nextDueDateState: TuitionDueDateState;
    atRisk: boolean;
  };
  latestObservedAt: string | null;
  hasCorrelatedStripeData: boolean;
};

const tuitionDashboardSnapshotCache = new Map<string, TuitionDashboardSnapshot>();
const tuitionFamilyLedgerSnapshotCache = new Map<string, TuitionFamilyLedgerSnapshot>();

const buildTuitionDashboardSnapshotCacheKey = (
  academicYear: string,
  options?: {
    limit?: number;
    searchQuery?: string;
  },
): string => {
  const normalizedSearchQuery = String(options?.searchQuery ?? '').trim().toLowerCase();
  return JSON.stringify({
    academicYear: academicYear.trim(),
    limit: options?.limit ?? 24,
    searchQuery: normalizedSearchQuery,
  });
};

const buildTuitionFamilyLedgerSnapshotCacheKey = (
  academicYear: string,
  familyId: string,
): string => JSON.stringify({
  academicYear: academicYear.trim(),
  familyId: familyId.trim(),
});

export const getCachedTuitionDashboardSnapshot = (
  academicYear: string,
  options?: {
    limit?: number;
    searchQuery?: string;
  },
): TuitionDashboardSnapshot | null => (
  tuitionDashboardSnapshotCache.get(
    buildTuitionDashboardSnapshotCacheKey(academicYear, options),
  ) ?? null
);

export const getCachedTuitionFamilyLedgerSnapshot = (
  academicYear: string,
  familyId: string,
): TuitionFamilyLedgerSnapshot | null => (
  tuitionFamilyLedgerSnapshotCache.get(
    buildTuitionFamilyLedgerSnapshotCacheKey(academicYear, familyId),
  ) ?? null
);

export const normalizeTuitionDashboardSnapshotFromTree = (
  raw: JsonRecord,
  fallbackAcademicYear: string,
): TuitionDashboardSnapshot => {
  const summary = asRecord(raw.summary);
  const dateRange = asRecord(raw.dateRange);
  const hasAuthoritativeTrend = Object.prototype.hasOwnProperty.call(raw, 'trend');
  const trend = (() => {
    if (!hasAuthoritativeTrend || !Array.isArray(raw.trend)) {
      return [];
    }
    const points = raw.trend.map((value) => asRecord(value));
    const numericKeys = [
      'tuition',
      'fees',
      'due',
      'outstanding',
      'collected',
      'billed',
      'billedLine',
      'seriesMax',
      'dueCents',
      'outstandingCents',
      'collectedCents',
      'billedCents',
    ] as const;
    const isValid = points.every((point) => (
      normalizeText(point.month).length > 0
      && /^\d{4}-\d{2}$/.test(normalizeText(point.monthKey))
      && numericKeys.every((key) => (
        typeof point[key] === 'number' && Number.isFinite(point[key])
      ))
    ));
    if (!isValid) {
      return [];
    }
    return points.map((point) => ({
      month: normalizeText(point.month),
      monthKey: normalizeText(point.monthKey),
      tuition: Number(point.tuition),
      fees: Number(point.fees),
      due: Number(point.due),
      outstanding: Number(point.outstanding),
      collected: Number(point.collected),
      billed: Number(point.billed),
      billedLine: Number(point.billedLine),
      seriesMax: Number(point.seriesMax),
      dueCents: Number(point.dueCents),
      outstandingCents: Number(point.outstandingCents),
      collectedCents: Number(point.collectedCents),
      billedCents: Number(point.billedCents),
    }));
  })();
  return {
    academicYear: normalizeText(raw.academicYear) || fallbackAcademicYear,
    families: asArray<JsonRecord>(raw.families).map((family) => normalizeFamilySnapshotFromTree(family)),
    totalBilledCents: Math.round(toNumber(summary.totalBilledCents)),
    totalCollectedCents: Math.round(toNumber(summary.totalCollectedCents)),
    outstandingCents: Math.round(toNumber(summary.outstandingCents)),
    currentDueCents: Math.round(toNumber(summary.currentDueCents)),
    overdueCents: Math.round(toNumber(summary.overdueCents)),
    autoPayFamilies: Math.max(Math.round(toNumber(summary.autoPayFamilies)), 0),
    collectionRate: toNumber(summary.collectionRate),
    trend,
    trendAuthority: hasAuthoritativeTrend ? 'server' : 'legacy-absent',
    hasCorrelatedStripeData: Boolean(raw.hasCorrelatedStripeData),
    latestObservedAt: normalizeIsoMoment(raw.latestObservedAt),
    isDataEmpty: Boolean(summary.isDataEmpty),
    emptyStateReason: summary.emptyStateReason === 'no-family-accounts'
      ? 'no-family-accounts'
      : summary.emptyStateReason === 'no-tuition-activity'
        ? 'no-tuition-activity'
        : summary.emptyStateReason === 'search-no-match'
          ? 'search-no-match'
          : null,
    dateRangeStart: normalizeIsoMoment(dateRange.start),
    dateRangeEnd: normalizeIsoMoment(dateRange.end),
  };
};

export const normalizeTuitionFamilyLedgerSnapshotFromTree = (
  raw: JsonRecord,
  fallbackAcademicYear: string,
): TuitionFamilyLedgerSnapshot => {
  const summary = asRecord(raw.summary);
  const family = normalizeFamilySnapshotFromTree(asRecord(raw.family));
  return {
    academicYear: normalizeText(raw.academicYear) || fallbackAcademicYear,
    family,
    ledgerEntries: asArray<JsonRecord>(raw.ledgerEntries).map((entry) => normalizeTransactionSnapshotFromTree(entry)),
    payers: asArray<JsonRecord>(raw.payers).map((payer) => normalizePayerSnapshotFromTree(payer)),
    documents: asArray<JsonRecord>(raw.documents).map((document) => normalizeDocumentSnapshotFromTree(document)),
    summary: {
      totalChargesCents: Math.round(toNumber(summary.totalChargesCents)),
      totalCreditsCents: Math.round(toNumber(summary.totalCreditsCents)),
      netBalanceCents: Math.round(toNumber(summary.netBalanceCents)),
      currentDueCents: Math.round(toNumber(summary.currentDueCents)),
      overdueCents: Math.round(toNumber(summary.overdueCents)),
      studentCount: Math.max(Math.round(toNumber(summary.studentCount)), 0),
      autoPayEnrolled: Boolean(summary.autoPayEnrolled),
      paymentMethodSummary: normalizeText(summary.paymentMethodSummary) || family.paymentMethodSummary,
      statusLabel: normalizeText(summary.statusLabel) || family.statusLabel,
      nextDueDate: normalizeOptionalText(summary.nextDueDate),
      nextDueDateLabel: normalizeText(summary.nextDueDateLabel) || family.nextDueDateLabel,
      nextDueDateState: summary.nextDueDateState === 'today'
        ? 'today'
        : summary.nextDueDateState === 'upcoming'
          ? 'upcoming'
          : summary.nextDueDateState === 'overdue'
            ? 'overdue'
            : null,
      atRisk: Boolean(summary.atRisk),
    },
    latestObservedAt: normalizeIsoMoment(raw.latestObservedAt),
    hasCorrelatedStripeData: Boolean(raw.hasCorrelatedStripeData),
  };
};

export const loadTuitionDashboardSnapshot = async (
  academicYear: string,
  options?: {
    limit?: number;
    searchQuery?: string;
  },
): Promise<TuitionDashboardSnapshot> => {
  const raw = await queryTuitionRouter<JsonRecord>('getSchoolTuitionDashboardTree', {
    academicYear,
    limit: options?.limit ?? 24,
    offset: 0,
    searchQuery: options?.searchQuery,
  });

  const snapshot = normalizeTuitionDashboardSnapshotFromTree(asRecord(raw), academicYear);
  tuitionDashboardSnapshotCache.set(
    buildTuitionDashboardSnapshotCacheKey(academicYear, options),
    snapshot,
  );
  return snapshot;
};

export const loadTuitionFamilyLedgerSnapshot = async (
  academicYear: string,
  familyId: string,
): Promise<TuitionFamilyLedgerSnapshot> => {
  const raw = await queryTuitionRouter<JsonRecord>('getSchoolTuitionFamilyLedgerTree', {
    academicYear,
    familyId,
  });

  const snapshot = normalizeTuitionFamilyLedgerSnapshotFromTree(asRecord(raw), academicYear);
  tuitionFamilyLedgerSnapshotCache.set(
    buildTuitionFamilyLedgerSnapshotCacheKey(academicYear, familyId),
    snapshot,
  );
  return snapshot;
};

export const loadParentTuitionSnapshot = async (
  academicYear: string,
): Promise<ParentTuitionSnapshot> => {
  const mirrorIndex = await loadTuitionMirrorCorrelationIndex();
  const managementContextRaw = await queryTuitionRouter('getMyPaymentManagementContext', {
    input: JSON.stringify({ moduleId: 'tuition' }),
  });
  const managementContext = asRecord(managementContextRaw);
  const familyId = normalizeOptionalText(managementContext.familyId);
  const managedHolder = asArray<JsonRecord>(managementContext.managedHolders)[0] ?? null;
  const familyName = normalizeOptionalText(managedHolder?.displayName);

  if (!familyId) {
    return {
      academicYear,
      familyId: null,
      familyName,
      totalChargesCents: 0,
      totalCreditsCents: 0,
      netBalanceCents: 0,
      overdueCents: 0,
      currentDueCents: 0,
      autoPayEnrolled: false,
      paymentMethodSummary: 'No linked family found for this parent account',
      paymentMethods: [],
      students: [],
      installmentSchedule: [],
      payerHolders: [],
      documents: [],
      transactions: [],
      hasCorrelatedStripeData: false,
      latestObservedAt: null,
    };
  }

  const [profilesRaw, balanceRaw, transactionsRaw, methodsRaw, documentsRaw] = await Promise.all([
    queryTuitionRouter('listPortalStudentProfiles', {
      input: JSON.stringify({ familyId }),
    }),
    queryTuitionRouter('getFamilyBalance', { familyId, academicYear }),
    queryTuitionRouter('listFamilyAccountTransactions', { familyId, academicYear }),
    queryTuitionRouter('listFamilyPaymentMethods', { familyId }),
    queryTuitionRouter('listFamilyDocuments', { familyId, limit: 24 }),
  ]);

  const balance = asRecord(balanceRaw);
  const rawTransactions = asArray<JsonRecord>(asRecord(transactionsRaw).items);
  const transactions = normalizeTransactions(rawTransactions, mirrorIndex);
  const paymentMethods = asArray<JsonRecord>(asRecord(methodsRaw).items).map((method) => normalizeParentPaymentMethodSnapshot(method));
  const rawDocuments = asArray<JsonRecord>(asRecord(documentsRaw).items);
  const profiles = asArray<JsonRecord>(asRecord(profilesRaw).items);
  const paymentMethodInfo = summarizePaymentMethods(paymentMethods);
  const totalChargesCents = Math.round(toNumber(balance.totalCharges));
  const totalCreditsCents = Math.round(toNumber(balance.totalCredits));
  const netBalanceCents = Math.round(toNumber(balance.netBalance));
  const openBalanceSummary = summarizeOpenBalance(rawTransactions);
  const overdueCents = openBalanceSummary.overdueCents;
  const currentDueCents = openBalanceSummary.currentDueCents;
  const { latestObservedAt } = resolveTransactionDateRange(transactions);

  const students = profiles.map((profile) => {
    const childInfo = asRecord(profile.childInfo);
    const studentId = normalizeText(profile.studentId ?? profile.id);
    const firstName = normalizeText(childInfo.firstName);
    const lastName = normalizeText(childInfo.lastName);
    const breakdown = buildStudentBreakdown(transactions, studentId);
    const subtotalCents = breakdown.reduce((sum, line) => sum + line.amountCents, 0);

    return {
      id: studentId,
      name: [firstName, lastName].filter(Boolean).join(' ') || 'Student',
      gradeLabel: formatGrade(profile.gradeApplyingFor),
      divisionLabel: formatDivision(profile.schoolDivision),
      amountDueCents: breakdown.length > 0 ? subtotalCents : null,
      subtotalCents,
      breakdown,
    };
  });
  const studentNameById = new Map(students.map((student) => [student.id, student.name] as const));
  const installmentSchedule = buildInstallmentSchedule(rawTransactions, studentNameById);
  const payerHolders = buildPayerSnapshots(
    asArray<JsonRecord>(managementContext.managedHolders).map((holder, index) => ({
      id: normalizeText(holder.id) || `managed-holder-${index + 1}`,
      holderType: normalizeText(holder.holderType) === 'parent'
        ? 'parent'
        : normalizeText(holder.holderType) === 'family'
          ? 'family'
          : 'third_party',
      displayName: normalizeText(holder.displayName) || 'Payer',
      relationLabel: normalizeText(holder.holderType) === 'family'
        ? 'Household'
        : toTitleCase(normalizeText(holder.holderType) || 'Payer'),
      email: normalizeOptionalText(holder.email),
      isPrimary: normalizeText(holder.id) === normalizeOptionalText(managementContext.defaultHolderId),
    })),
    netBalanceCents,
  );
  const documents = [
    ...buildDocumentSnapshots(rawDocuments, studentNameById),
    ...buildDerivedReceiptArtifacts(transactions, studentNameById),
  ].sort((left, right) => sortIsoDescending(left.uploadedAt, right.uploadedAt));

  return {
    academicYear,
    familyId,
    familyName,
    totalChargesCents,
    totalCreditsCents,
    netBalanceCents,
    overdueCents,
    currentDueCents,
    autoPayEnrolled: paymentMethodInfo.autoPayEnrolled,
    paymentMethodSummary: paymentMethodInfo.paymentMethodSummary,
    paymentMethods,
    students,
    installmentSchedule,
    payerHolders,
    documents,
    transactions,
    hasCorrelatedStripeData: transactions.some((transaction) => transaction.isStripeCorrelated),
    latestObservedAt,
  };
};
